<template>
  <Modal
    v-model="dialogVisible"
    class-name="over-rule-dialog"
    :title="dialogTitle"
    width="980"
    :mask-closable="false"
    @on-cancel="handleClose"
  >
    <div class="over-rule-body">
      <Spin v-if="detailLoading" fix size="large" />
      <div class="over-rule-panel">
        <div class="over-rule-field">
          <div class="over-rule-field-label">本次送审版本</div>
          <div class="over-rule-field-value">{{ detail.submitVersion }}</div>
        </div>
        <div class="over-rule-field">
          <div class="over-rule-field-label">审核结果时间</div>
          <div class="over-rule-field-value">{{ detail.resultTime }}</div>
        </div>
        <div class="over-rule-field over-rule-field--reason">
          <div class="over-rule-field-label over-rule-field-label--emphasis">
            完整驳回原因
          </div>
          <div class="over-rule-field-value over-rule-field-value--emphasis">
            {{ detail.fullRejectReason }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button @click="handleClose">关闭</Button>
      <Button
        v-if="showInitiateAppealButton"
        :disabled="!canInitiateAppealFromRow"
        @click="handleAppeal"
      >
        发起申诉
      </Button>
      <Button
        type="primary"
        :disabled="modifySubmitDisabled"
        @click="handleModifySubmit"
      >
        修改送审资料
      </Button>
    </template>
  </Modal>
</template>

<script>
import dayjs from 'dayjs'
import { getTtMediaReviewVersions } from '@/api/short-review'
import { canInitiateAppeal } from '../../utils/appeal'
import {
  buildOverRuleDetailFromVersionRecord,
  pickLatestRejectedVersionRecord
} from '../../utils/reviewVersions'

function formatResultTime(timeStr) {
  if (!timeStr) return '—'
  const parsed = dayjs(String(timeStr).replace(/\//g, '-'))
  return parsed.isValid() ? parsed.format('YYYY/MM/DD HH:mm') : String(timeStr)
}

function formatSubmitVersion(row) {
  if (!row) return '—'
  const rejectVersion = row.rejectVersion
  if (rejectVersion != null && rejectVersion !== '') {
    const text = String(rejectVersion).trim()
    return /^V/i.test(text) ? text.toUpperCase() : `V${text}`
  }
  const ttVersion = row.reviewTtVersion
  if (ttVersion != null && Number(ttVersion) > 0) {
    return `V${ttVersion}`
  }
  return '—'
}

function pickFullRejectReason(row) {
  if (!row) return '—'
  const fields = [
    row.rejectReason,
    row.reviewRejectReason,
    row.rejectDesc,
    row.lastRejectReason,
    row.reviewRejectDesc
  ]
  for (let i = 0; i < fields.length; i += 1) {
    const text = (fields[i] || '').trim()
    if (text) return text
  }
  return '—'
}

/** 从列表行解析驳回详情（兼容后续接口字段扩展） */
export function parseOverRuleDetail(row) {
  const fullRejectReason = pickFullRejectReason(row)
  return {
    submitVersion: formatSubmitVersion(row),
    resultTime: formatResultTime(
      row.reviewResultTime || row.reviewUpdatedAt || row.reviewTimeRaw
    ),
    fullRejectReason
  }
}

export default {
  name: 'OverRuleDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      detailLoading: false,
      /** 版本记录接口解析出的最新驳回详情 */
      versionDetail: null
    }
  },
  computed: {
    dialogTitle() {
      const name = (this.row && this.row.playName) || '—'
      return `处理驳回 · ${name}`
    },
    detail() {
      if (this.versionDetail) return this.versionDetail
      return parseOverRuleDetail(this.row)
    },
    canInitiateAppealFromRow() {
      return canInitiateAppeal(this.row)
    },
    /** 列表态为申诉中（reviewStatus 5） */
    isReviewAppealing() {
      const row = this.row || {}
      if (row.reviewStatusApi != null && row.reviewStatusApi !== '') {
        return Number(row.reviewStatusApi) === 5
      }
      return row.reviewStatus === 'appealing'
    },
    /** 已驳回-申诉未通过（reviewStatus 7，不可二次申诉） */
    isAppealRejected() {
      const row = this.row || {}
      if (row.reviewStatusApi != null && row.reviewStatusApi !== '') {
        return Number(row.reviewStatusApi) === 7
      }
      return row.reviewStatus === 'appeal_rejected'
    },
    /** 申诉中、申诉未通过时不展示发起申诉 */
    showInitiateAppealButton() {
      return !this.isReviewAppealing && !this.isAppealRejected
    },
    /** 驳回版本接口加载中、申诉中不可修改送审资料 */
    modifySubmitDisabled() {
      if (this.detailLoading) return true
      return this.isReviewAppealing
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.loadRejectVersionDetail()
      } else {
        this.versionDetail = null
        this.detailLoading = false
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    },
    'row.id'(val, oldVal) {
      if (!this.dialogVisible || val == null) return
      if (oldVal == null || oldVal === '') return
      if (val === oldVal) return
      this.loadRejectVersionDetail()
    }
  },
  methods: {
    loadRejectVersionDetail() {
      const id = this.row && this.row.id
      if (id == null || id === '') {
        this.versionDetail = null
        return
      }
      this.detailLoading = true
      this.versionDetail = null
      getTtMediaReviewVersions(id)
        .then((res) => {
          if (res.code !== 200 || !res.data) {
            this.$Message.error(res.message || '获取审核版本记录失败')
            return
          }
          const record = pickLatestRejectedVersionRecord(res.data.items)
          this.versionDetail = buildOverRuleDetailFromVersionRecord(record)
        })
        .catch(() => {
          this.$Message.error('获取审核版本记录失败')
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    buildActionPayload() {
      const { submitVersion, fullRejectReason, ttVersion } = this.detail
      return {
        ...this.row,
        rejectVersion: submitVersion,
        rejectReason: fullRejectReason === '—' ? '' : fullRejectReason,
        ttVersion:
          ttVersion != null && ttVersion !== ''
            ? ttVersion
            : this.row.reviewTtVersion
      }
    },
    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
    },
    handleAppeal() {
      if (!this.canInitiateAppealFromRow) {
        this.$Message.warning('该剧已发起过申诉或当前状态不可申诉')
        return
      }
      this.$emit('open-appeal', this.buildActionPayload())
    },
    handleModifySubmit() {
      if (this.detailLoading) return
      if (this.modifySubmitDisabled) {
        this.$Message.info('申诉中不可修改送审资料')
        return
      }
      this.$emit('modify-submit', this.buildActionPayload())
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.over-rule-body {
  position: relative;
  box-sizing: border-box;
}

.over-rule-panel {
  box-sizing: border-box;
  padding: 20px 24px 20px 20px;
  background: #fff8f8;
  border-left: 4px solid #de5a50;
  border-radius: 0 4px 4px 0;
}

.over-rule-field {
  margin-bottom: 16px;
}

.over-rule-field:last-child {
  margin-bottom: 0;
}

.over-rule-field-label {
  margin-bottom: 6px;
  font-size: 13px;
  line-height: 20px;
  color: #909399;
}

.over-rule-field-label--emphasis {
  color: #8a98a8;
  font-weight: 400;
}

.over-rule-field-value {
  font-size: 14px;
  line-height: 22px;
  color: #303133;
  word-break: break-word;
}

.over-rule-field-value--emphasis {
  font-size: 14px;
  line-height: 22px;
  color: #de5a50;
}

.over-rule-field--reason {
  margin-bottom: 0;
}
</style>

<style>
/* View Design：class-name 挂在 .ivu-modal-wrap 上，flex 垂直水平居中 */
.over-rule-dialog {
  display: flex;
  align-items: center;
  justify-content: center;
}

.over-rule-dialog .ivu-modal {
  top: 0;
}

.over-rule-dialog .ivu-modal-content {
  display: flex;
  flex-direction: column;
  height: auto;
  max-height: calc(100vh - 48px);
  box-sizing: border-box;
}

.over-rule-dialog .ivu-modal-header {
  flex-shrink: 0;
  height: 76px;
  box-sizing: border-box;
  padding: 0 24px;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  align-items: center;
}

.over-rule-dialog .ivu-modal-header-inner {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #263f58;
}

.over-rule-dialog .ivu-modal-close {
  top: 22px;
  right: 20px;
  z-index: 1;
}

.over-rule-dialog .ivu-modal-body {
  flex: 0 1 auto;
  height: auto;
  min-height: 0;
  padding: 24px;
  overflow: auto;
  box-sizing: border-box;
}

.over-rule-dialog .ivu-modal-footer {
  flex-shrink: 0;
  height: 66px;
  box-sizing: border-box;
  padding: 0 24px;
  border-top: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.over-rule-dialog .ivu-modal-footer .ivu-btn {
  height: 35px;
  padding: 8px 15px;
  font-size: 13px;
  line-height: 17px;
}

.over-rule-dialog .ivu-modal-footer .ivu-btn + .ivu-btn {
  margin-left: 8px;
}
</style>
